<template>
  <div
    class='box'
    :data-id='isDevelopment ? id : null'
  >
    <a class='overlay' :href='url' @click='trackSelecteBox' />
    <div class='inner'>
      <div
        class='image'
        :class='{ [sizeWithXl]: true }'
      />
      <div class='details'>
        <div class='name'>
          {{ name }}
          <div class='size-tag'>
            {{ t(`frontend.storage_schema_boxes.index.sizes.${size}.title`) }}
          </div>
        </div>
        <div
          v-if='discounts.length'
          class='discounts'
        >
          <template
            v-for='(discount, index) in discounts'
            :key='index'
          >
            <span v-html='discount' />
            <span
              v-if='index != discounts.length - 1'
              class="separator"
            />
          </template>
        </div>
        <div class='dimensions'>
          <span>{{ squareText }}</span>
          <span class="separator" />
          <span>{{ dimensionsText }}</span>
          <span class="separator" />
          <span>{{ capacityText }}</span>
        </div>
        <div class='price'>
          {{ tp('from') }}
          <span
            v-if='priceWoDiscount != priceWithDiscount'
            class='price-wo-discount'
          >
            {{ priceWoDiscount }}
          </span>
          {{ priceWithDiscount }}{{ tp('month') }}
        </div>
        <div>
          <a :href='orderUrl' class='button' @click='trackRentBox'>
            {{ tp('rent') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StorageBox',
  inheritAttrs: false
};
</script>

<script setup>
import { ref, watch } from 'vue';

import track from '@/helpers/track';
import { t, prepared } from '@/helpers/i18n';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, required: true },
  size: { type: String, required: true },
  capacity: { type: Number, required: true },
  discounts: { type: Array, required: true },
  squareText: { type: String, required: true },
  dimensionsText: { type: String, required: true },
  capacityText: { type: String, required: true },
  priceWoDiscount: { type: String, required: true },
  priceWithDiscount: { type: String, required: true },
  url: { type: String, required: true },
  orderUrl: { type: String, required: true }
});
const tp = prepared('frontend.storage_schema_boxes.box');

const isDevelopment = window.ENV === 'development';

const XL_CAPACITY = 25.0;

const sizeWithXl = props.capacity > XL_CAPACITY ?
  'extra_large' :
  props.size;

async function trackSelecteBox(e) {
  track({ event: 'select_box' });
}
async function trackRentBox(e) {
  track({ event: 'rent_box' });
}
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'
@import 'app/packs/stylesheets/mixins/button_v4'

$image_path: '../../../../images/vue/storage_schema_boxes/box'

.box
  position: relative
  border-top: 1px solid var(--gray-300)
  color: var(--gray-900)

  +gte_laptop
    &:hover
      color: var(--gray-900)
      text-decoration: none

      .name
        color: var(--primary-color)

  &:active
    color: var(--gray-900)
    text-decoration: none

    .name
      color: var(--primary-color-active)

.overlay
  bottom:0
  left:0
  position: absolute
  right:0
  top:0

.inner
  display: flex
  pointer-events: none
  position: relative
  z-index: 1

  +lte_ipad
    gap: rem4(16px)
    padding: rem4(16px) 0

  +gte_laptop
    gap: 24px
    padding: 16px 0
    align-items: center

.image
  background-position: center
  background-repeat: no-repeat
  background-size: cover

  +lte_ipad
    height: rem4(90px)
    width: rem4(90px)

  +gte_laptop
    height: 90px
    width: 90px

  @each $size in ultra_small, small, medium, large, extra_large
    &.#{$size}
      background-image: url('#{$image_path}/size/desktop/#{$size}.png')

.details
  display: flex
  flex-direction: column

  +lte_ipad
    gap: rem4(8px)

  +gte_laptop
    gap: 8px

.name
  color: #000
  display: flex
  transition: color var(--transition-duration)

  +lte_ipad
    +p4(700)
    gap: rem4(8px)

  +gte_laptop
    +p3(700)
    gap: 8px

  .size-tag
    +p4
    align-items: center
    background: var(--gray-100)
    color: var(--gray-900)
    display: flex
    justify-content: center

    +lte_ipad
      border-radius: 100px
      height: rem4(24px)
      padding: 0 rem4(8px)

    +gte_laptop
      border-radius: 100px
      height: 24px
      padding: 0 8px

.discounts
  color: #08cc33
  flex-wrap: wrap

  .separator
    background: #08cc33

.discounts,
.dimensions
  display: flex

  +lte_ipad
    +p5(500)
    gap: rem4(8px)

  +gte_laptop
    +p4(500)
    gap: 8px

.separator
  align-self: center
  background: var(--gray-900)

  +lte_ipad
    width: rem4(2px)
    height: rem4(2px)

  +gte_laptop
    width: 2px
    height: 2px

.price
  display: flex

  +lte_ipad
    +p5(700)
    gap: rem4(8px)

  +gte_laptop
    +p4(700)
    gap: 8px

  span
    color: var(--gray-500)
    font-weight: 500
    text-decoration: line-through

.button
  +button_v4('secondary', 'sm')
  pointer-events: all
</style>
